import { useQuery } from '@tanstack/react-query';

import { getCppInvoiceTransactions } from '~/services/LuxLoyaltyService';
import type { CppInvoiceTransactionsQuery } from '~/services/LuxLoyaltyService.types';

import { costPerPointQueryKeys } from './costPerPointQueryKeys';

interface UseCppInvoiceTransactionsOptions {
  enabled?: boolean;
}

/**
 * Fetches one page of per-transaction CPP invoice detail.
 *
 * Keeps the previous page on screen while the next one loads, so paging through the grid does not
 * flash an empty table. Every page carries the same whole-period `totals`/`blockers`, so the
 * summary above the grid stays stable across page changes.
 */
export function useCppInvoiceTransactions(
  query: Partial<CppInvoiceTransactionsQuery>,
  options: UseCppInvoiceTransactionsOptions = {},
) {
  const { enabled = true } = options;
  const isComplete = Boolean(query.partnerId && query.start && query.end);

  return useQuery({
    queryKey: costPerPointQueryKeys.invoiceTransactions(query as CppInvoiceTransactionsQuery),
    queryFn: async ({ signal }) => {
      return await getCppInvoiceTransactions(query as CppInvoiceTransactionsQuery, { signal });
    },
    placeholderData: (previousData) => previousData,
    enabled: enabled && isComplete,
    retry: false,
  });
}
